<template>
  <aside class="document-page">
    <DocumentPageTitle title="Catalytic Projects" />

    <template v-if="filteredProjects && filteredProjects.length">
      <template
        v-for="(project, projectIndex) in filteredProjects"
        :key="project.id"
      >
        <section class="document-section">
          <div :class="{'document-section-container' : projectIndex !== filteredProjects.length -1}">
            <div class="document-section-title-wrapper">
              <div>
                <h4 class="document-section-subtitle">
                  Project
                </h4>

                <h2 class="!mb-2">
                  {{ project.label }}
                </h2>

                <div class="mt-0 flex flex-row items-center md:pt-1 flex-shrink-0 gap-2 md:gap-3">
                  <template v-if="frameworks && project.data.alignments && project.data.alignments.length">
                    <template
                      v-for="(categories, index) in getFilteredFrameworkCategories(project.data.alignments)"
                      :key="index"
                    >
                      <div class="flex gap-2">
                        <template
                          v-for="category in categories"
                          :key="category.id"
                        >
                          <AppTooltip class="mb-5 document-tooltip">
                            <template #reference>
                              <CategoryIcon
                                class="cursor-pointer flex-shrink-0 [&_img]:!w-9 [&_img]:!h-9"
                                :name="category.shortName"
                                :icon="category.icon"
                                :background-color="category.backgroundColor"
                                tooltip-content="Icon Tooltip"
                                @click="categoryToShow = category"
                              />
                            </template>

                            <template #default>
                              <div class="min-w-64 text-center">
                                <p class="mb-0.5">
                                  <span class="font-semibold">Framework:</span> {{ category.framework.shortName }}
                                </p>
                                <p class="mb-0.5">
                                  <span class="font-semibold">Category:</span> {{ category.shortName }}
                                </p>
                              </div>
                            </template>
                          </AppTooltip>
                        </template>
                      </div>
                    </template>
                  </template>
                </div>
              </div>
            </div>

            <DocumentProjectDetailsTable :project-data="project.data" />

            <template
              v-if="
                project.data.householdNumber ||
                  project.data.populationNumber ||
                  project.data.wypdNumber ||
                  project.data.temporaryJobNumber ||
                  project.data.permanentJobNumber
              "
            >
              <h2>Development Impact</h2>

              <table class="table-project-details mb-4">
                <tbody>
                  <tr
                    v-if="project.data.householdNumber"
                  >
                    <td>
                      <div class="flex gap-4 p-3">
                        <div>
                          1.
                        </div>

                        <div class="flex flex-col">
                          <div class="pb-2">
                            What is the estimated number of households that will benefit from this project?
                          </div>

                          <span>
                            {{ stringFormatter.formatNumbers(project.data.householdNumber) }}
                          </span>
                        </div>
                      </div>
                    </td>
                  </tr>

                  <tr v-if="project.data.populationNumber">
                    <td>
                      <div class="flex gap-4 p-3">
                        <div>
                          2.
                        </div>

                        <div class="flex flex-col">
                          <div class="pb-2">
                            What is the estimated number of people in your district/metro who will benefit from this project?
                          </div>

                          <span>
                            {{ stringFormatter.formatNumbers(project.data.populationNumber) }}
                          </span>
                        </div>
                      </div>
                    </td>
                  </tr>
                  <tr
                    v-if="project.data.wypdNumber"
                  >
                    <td>
                      <div class="flex gap-4 p-3">
                        <div>
                          3.
                        </div>

                        <div class="flex flex-col">
                          <div class="pb-2">
                            What is the estimated number of Women, Youth and Persons with Disabilities (WYPD) who will benefit from this project?
                          </div>

                          <span>
                            {{ stringFormatter.formatNumbers(project.data.wypdNumber) }}
                          </span>
                        </div>
                      </div>
                    </td>
                  </tr>
                  <tr
                    v-if="project.data.temporaryJobNumber"
                  >
                    <td>
                      <div class="flex gap-4 p-3">
                        <div>
                          4.
                        </div>

                        <div class="flex flex-col">
                          <div class="pb-2">
                            What is the estimated number of temporary or construction jobs that will be created by this project?
                          </div>

                          <span>
                            {{ stringFormatter.formatNumbers(project.data.temporaryJobNumber) }}
                          </span>
                        </div>
                      </div>
                    </td>
                  </tr>
                  <tr
                    v-if="project.data.permanentJobNumber"
                  >
                    <td>
                      <div class="flex gap-4 p-3">
                        <div>
                          5.
                        </div>

                        <div class="flex flex-col">
                          <div class="pb-2">
                            What is the estimated number of permanent jobs that will be created by this project?
                          </div>

                          <span>
                            {{ stringFormatter.formatNumbers(project.data.permanentJobNumber) }}
                          </span>
                        </div>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </template>

            <div
              v-if="project?.data.writeUp && project?.data.writeUp.length"
              v-html="project?.data.writeUp"
            />
          </div>
          <template v-if="project.data.latitude && project.data.longitude">
            <h2 class="mt-5">
              Project Location
            </h2>
            <div
              class="h-[350px] relative"
            >
              <LMap
                ref="map"
                :zoom="8"
                :center="[project.data.latitude, project.data.longitude]"
                :options="{ attributionControl: false, scrollWheelZoom: false }"
                :use-global-leaflet="false"
                style="z-index: 1;"
              >
                <LTileLayer
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  name="OpenStreetMap"
                  :no-wrap="true"
                />

                <LMarker
                  :auto-pan="true"
                  :auto-pan-padding="50"
                  :auto-pan-speed="100"
                  :lat-lng="[project.data.latitude, project.data.longitude]"
                />
              </LMap>
            </div>
          </template>
        </section>
      </template>
    </template>

    <template v-else>
      <template v-if="!selectedCategories.length">
        <h3 class="block text-center font-semibold">
          There are no Categories selected. Please select some categories to view Projects.
        </h3>
      </template>

      <template v-else>
        <h3 class="block text-center font-semibold">
          There are no Projects for the selected categories.
        </h3>
      </template>
    </template>
  </aside>

  <template v-if="categoryToShow">
    <AppModal
      modal-width="max-w-2xl"
      @close="categoryToShow = null"
    >
      <template #title>
        <CategoryHeader
          :category="categoryToShow"
          @close="categoryToShow = null"
        />
      </template>

      <div
        class="px-6 pt-6 pb-12 leading-6"
        v-html="categoryToShow?.writeUp"
      />
    </AppModal>
  </template>
</template>

<script setup>
import { computed, ref, watch } from 'vue';

import { AppTooltip } from '@/components/app';
import { CategoryIcon } from '@/components/category';
import { AppModal } from '@/components/modals';
import { CategoryHeader } from '@/components/modals/headers';
import { DocumentPageTitle, DocumentProjectDetailsTable } from '@/components/plan-document';
import { LMap, LMarker, LTileLayer } from '@vue-leaflet/vue-leaflet';
import { useStringFormatter } from '@/composables/index.js';

const stringFormatter = useStringFormatter();

const props = defineProps({
  projects: {
    type: Array,
    default: () => [],
  },
  frameworks: {
    type: Object,
    default: () => ({}),
  },
  selectedFramework: {
    type: Object,
    default: () => null,
  },
  selectedCategories: {
    type: Array,
    default: () => [],
  },
});

const categoryToShow = ref(null);

// This filters the projects based on whether the categories in the framework filter are present on the project.
const filteredProjects = computed(() => {
  return props.projects.filter(project => {
    // Transforming the category ids into an object that can be used for comparison with the `selectedCategories` from the document filters
    const categoryIds = project.data.alignments.flatMap(alignment => alignment.categories.length > 0 ? alignment.categories : null);

    // If a framework has been selected, only return categories for the current project that match the filtered categories for the selected framework
    if (props.selectedFramework) {
      return categoryIds.some(catIds => props.selectedCategories.includes(catIds));
    }

    // Return all the projects when no framework is selected
    return true;
  });
});

const getFrameworkCategoriesForOutcome = (frameworkCategories) => {
  return frameworkCategories.map(frameworkCategory => {
    // Find the framework that matches the category's framework ID
    const framework = props.frameworks.find(framework => framework.id === frameworkCategory.framework);

    const categoriesForFramework = [];

    framework.categories.forEach(category => {
      frameworkCategory.categories.filter((item) => {
        if (item === category.id) {
          categoriesForFramework.push(category);
        }
      });
    });

    return categoriesForFramework;
  });
};

const getFilteredFrameworkCategories = frameworkCategories => {
  const categories = getFrameworkCategoriesForOutcome(frameworkCategories);

  if (props.selectedFramework) {
    // Filter categories to only include those from the selected framework
    return categories.filter(frameworkCategory => frameworkCategory[0]?.framework?.id === props.selectedFramework.id);
  }

  // Return all categories if no framework is selected
  return categories;
};

const setBodyOverflow = isHidden => {
  if (isHidden) {
    document.body.style.overflow = 'hidden';
  } else {
    document.body.style.overflow = 'auto';
  }
};

watch(categoryToShow, (newVal) => {
  setBodyOverflow(!!newVal);
});

</script>
